<script setup>
import { useAbility } from '@casl/vue'
import { useApi } from '@/utils/api'

/**
 * pages/settings/ai-status.vue — Modul 24
 *
 * Halaman kecil untuk owner/admin memantau kesehatan whisper-server &
 * PaddleOCR service TANPA perlu akses SSH/terminal server. Dibatasi CASL
 * subject 'Settings' (permission Settings.read) — permission yang sama
 * dengan halaman settings lain, TIDAK membuat permission baru.
 *
 * PENTING: baik Voice-to-Order maupun Receipt Scanner SELALU punya jalur
 * manual yang berfungsi normal terlepas dari status di halaman ini —
 * halaman ini murni informasi, bukan kill-switch atau gerbang akses.
 */
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Settings',
  },
})

const ability = useAbility()
const { loading, request } = useApiRequest()

const voiceStatus = ref(null)
const receiptStatus = ref(null)
const lastFetchedAt = ref(null)

let pollTimer = null

const load = async () => {
  const res = await request(() => useApi('/api/v1/settings/ai-status').get())
  const data = res?.data ?? res

  if (data) {
    voiceStatus.value = data.voice_to_order
    receiptStatus.value = data.receipt_scanner
    lastFetchedAt.value = new Date()
  }
}

const statusMeta = status => {
  switch (status) {
    case 'healthy':
      return { color: 'success', icon: 'ri-checkbox-circle-fill', label: 'Normal' }
    case 'down':
      return { color: 'error', icon: 'ri-close-circle-fill', label: 'Tidak Merespons' }
    default:
      return { color: 'grey', icon: 'ri-question-line', label: 'Belum Diketahui' }
  }
}

const formatCheckedAt = iso => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('id-ID', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

onMounted(() => {
  load()
  // Auto-refresh tiap 30 detik — cache backend sendiri di-refresh scheduler
  // tiap 1 menit (CheckAiServicesHealth), jadi halaman ini tidak akan pernah
  // membaca data yang lebih basi dari itu.
  pollTimer = setInterval(load, 30000)
})

onUnmounted(() => clearInterval(pollTimer))
</script>

<template>
  <div>
    <h5 class="text-h5 mb-1">
      Status Layanan AI
    </h5>
    <p class="text-body-2 text-medium-emphasis mb-6">
      Pemantauan kesehatan layanan Voice-to-Order & Smart Receipt Scanner.
      Kedua fitur ini SELALU punya jalur manual yang berfungsi normal,
      apa pun status di halaman ini.
    </p>

    <VRow>
      <VCol cols="12" md="6">
        <VCard>
          <VCardText class="d-flex align-center">
            <VIcon icon="ri-mic-line" size="32" class="me-4 text-medium-emphasis" />
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-medium">
                Voice-to-Order
              </div>
              <div class="text-caption text-medium-emphasis">
                Terakhir dicek: {{ formatCheckedAt(voiceStatus?.checked_at) }}
              </div>
            </div>
            <VChip
              :color="statusMeta(voiceStatus?.status).color"
              :prepend-icon="statusMeta(voiceStatus?.status).icon"
              variant="tonal"
            >
              {{ statusMeta(voiceStatus?.status).label }}
            </VChip>
          </VCardText>

          <VCardText v-if="voiceStatus?.status !== 'healthy'" class="pt-0">
            <VAlert type="warning" variant="tonal" density="compact">
              {{ voiceStatus?.message }}
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard>
          <VCardText class="d-flex align-center">
            <VIcon icon="ri-camera-line" size="32" class="me-4 text-medium-emphasis" />
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-medium">
                Smart Receipt Scanner
              </div>
              <div class="text-caption text-medium-emphasis">
                Terakhir dicek: {{ formatCheckedAt(receiptStatus?.checked_at) }}
              </div>
            </div>
            <VChip
              :color="statusMeta(receiptStatus?.status).color"
              :prepend-icon="statusMeta(receiptStatus?.status).icon"
              variant="tonal"
            >
              {{ statusMeta(receiptStatus?.status).label }}
            </VChip>
          </VCardText>

          <VCardText v-if="receiptStatus?.status !== 'healthy'" class="pt-0">
            <VAlert type="warning" variant="tonal" density="compact">
              {{ receiptStatus?.message }}
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <div class="d-flex justify-end mt-4">
      <VBtn variant="text" prepend-icon="ri-refresh-line" :loading="loading" @click="load">
        Cek Ulang Sekarang
      </VBtn>
    </div>
  </div>
</template>
